import React from "react";
import { Facebook, Linkedin, Mail, Phone } from "lucide-react";
import { motion } from "framer-motion";
import XLogo from "../common/XLogo";
import { Link } from "react-router-dom";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = [
    {
      title: "For Candidates",
      links: [
        { name: "Find a Job", path: "/job-search" },
        { name: "Create Profile", path: "/signup" },
        { name: "Career Advice", path: "#" },
        { name: "Job Alerts", path: "#" },
      ],
    },
    {
      title: "For Employers",
      links: [
        { name: "Post a Job", path: "/signup?type=recruiter" },
        { name: "Search Resumes", path: "/pricing" },
        { name: "Pricing", path: "/pricing" },
        { name: "Recruiting Solutions", path: "#" },
      ],
    },
    {
      title: "Company",
      links: [
        { name: "About Us", path: "/about" },
        { name: "Contact Us", path: "/contact" },
        { name: "Privacy Policy", path: "/privacy" },
        { name: "Terms of Service", path: "/terms" },
      ],
    },
  ];

  // Animation for each card
  const cardVariants = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.5 } },
    hover: {
      y: -8,
      boxShadow: "0px 8px 25px rgba(0, 150, 255, 0.25)",
      transition: { type: "spring", stiffness: 200, damping: 15 },
    },
  };

  // Animation for links inside each card
  const linkVariants = {
    initial: { opacity: 0, y: 12 },
    animate: (i) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.08 },
    }),
  };

  return (
    <footer className="bg-secondary-900 text-white pl-0 sm:pl-[72px]">
      <div className="container mx-auto px-6 pt-10 pb-5">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">

          {/* Brand */}
          <div className="lg:col-span-4 space-y-6">
            <a href="/" className="flex items-center space-x-2">
              <img src="/logo.png" alt="WinguPort Logo" className="h-12 w-12" />
              <span className="text-3xl font-extrabold tracking-tight">
                <span className="text-wingu-light-blue">Wingu</span>
                <span className="text-wingu-dark-blue">Port</span>
              </span>
            </a>

            <p className="text-secondary-400 max-w-sm">
              Connecting the world's best aviation talent with premier 
              opportunities.
            </p>

            <div className="flex space-x-4 mt-4">
              <a className="p-2 bg-secondary-800 rounded-full hover:bg-primary-600 transition transform hover:scale-110">
                <Facebook size={20} />
              </a>
              <a className="p-2 bg-secondary-800 rounded-full hover:bg-primary-600 transition transform hover:scale-110">
                <XLogo />
              </a>
              <a className="p-2 bg-secondary-800 rounded-full hover:bg-primary-600 transition transform hover:scale-110">
                <Linkedin size={20} />
              </a>
            </div>
          </div>

          {/* Animated Footer Link Cards */}
          <div className="lg:col-span-8 grid grid-cols-2 md:grid-cols-3 gap-8">

            {footerLinks.map((section, index) => (
              <motion.div
                key={section.title}
                className="relative overflow-hidden p-6 bg-secondary-800/60 rounded-xl border border-secondary-700"
                variants={cardVariants}
                initial="initial"
                whileInView="animate"
                whileHover="hover"
                viewport={{ once: true }}
              >
                {/* Radar Sweep Animation */}
                <motion.div
                  className="absolute inset-0 w-full h-full pointer-events-none"
                  initial={{ x: "-120%" }}
                  whileHover={{ x: "120%" }}
                  transition={{ duration: 1.2, ease: "easeInOut" }}
                  style={{
                    background:
                      "linear-gradient(120deg, transparent 0%, rgba(0,180,255,0.15) 50%, transparent 100%)",
                  }}
                />

                <h4 className="font-semibold text-base mb-4 uppercase tracking-wide">
                  {section.title}
                </h4>

                <ul className="space-y-3 relative z-10">
                  {section.links.map((link, i) => (
                    <motion.li
                      key={link.name}
                      variants={linkVariants}
                      custom={i}
                      initial="initial"
                      whileInView="animate"
                      viewport={{ once: true }}
                    >
                      <Link
                        to={link.path}
                        className="text-secondary-400 hover:text-primary-400 hover:underline transition-colors text-sm"
                      >
                        {link.name}
                      </Link>
                    </motion.li>
                  ))}
                </ul>
              </motion.div>
            ))}

          </div>
        </div>
      </div>

      {/* Runway Bar */}
      <div className="my-10">
        <div className="mb-6 h-3 w-full flex rounded overflow-hidden shadow-lg">
          <div className="flex-[4] bg-black animate-pulse"></div>
          <div className="flex-[1] bg-white"></div>
          <div className="flex-[4] bg-red-600 animate-pulse"></div>
          <div className="flex-[1] bg-white"></div>
          <div className="flex-[4] bg-green-600 animate-pulse"></div>
        </div>

        <div className="text-center text-secondary-500 text-sm">
          <p>
            &copy; {currentYear}{" "}
            <span className="font-semibold text-white">
              WinguPort Aviation Workforce Recruitment System
            </span>
            . All Rights Reserved.
          </p>

          <div className="mt-2 flex items-center justify-center space-x-6 text-secondary-400 text-sm">
            <div className="flex items-center space-x-2">
              <Mail size={16} /> <span>support@winguport.com</span>
            </div>
            <div className="flex items-center space-x-2">
              <Phone size={16} /> <span>+254 700 123 456</span>
            </div>
          </div>
        </div>
      </div>

    </footer>
  );
};

export default Footer;
